import React, { useCallback, useEffect, useState } from 'react';
import { getSalesPriceList } from '../../services/salesService';
import { salesOfferService } from '../../services/salesOfferService';
import ConfirmModal from '../Common/ConfirmModal';
import MutateGate from '../Common/MutateGate';

const OFFER_KINDS = [
  {
    value: 'discount',
    label: 'Descuento',
    description: 'Un solo producto con porcentaje de descuento.',
  },
  {
    value: 'multi_product',
    label: 'Varios productos',
    description: 'Combo general: precio de oferta y % de descuento; cantidad por producto.',
  },
];

const CheckIcon = () => (
  <svg className="h-3.5 w-3.5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const PriceListOffersSection = ({ activePosId, onToast }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerator, setShowGenerator] = useState(false);
  const [editingOfferId, setEditingOfferId] = useState(null);
  const [toggleTarget, setToggleTarget] = useState(null);

  const [form, setForm] = useState({
    name: '',
    offer_kind: 'discount',
    discount_type: 'percent',
    discount_value: '10',
    bundle_offer_price: '',
    status: 'draft',
    notes: '',
  });
  const [catalog, setCatalog] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [productQuantities, setProductQuantities] = useState({});
  const [preview, setPreview] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');

  const isDiscountOffer = form.offer_kind === 'discount';
  const isMultiProduct = form.offer_kind === 'multi_product';

  const loadOffers = useCallback(async () => {
    if (!activePosId) {
      setOffers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await salesOfferService.getOffers({ point_of_sale_id: Number(activePosId) });
      if (res.success) setOffers(res.data || []);
    } catch {
      onToast('Error al cargar ofertas', 'error');
    } finally {
      setLoading(false);
    }
  }, [activePosId, onToast]);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  const loadCatalog = useCallback(async () => {
    if (!activePosId) {
      setCatalog([]);
      return;
    }
    try {
      const res = await getSalesPriceList(Number(activePosId), catalogSearch, 1, 200, 'prices');
      if (res.success) {
        setCatalog((res.data || []).filter((p) => p.price != null));
      }
    } catch {
      onToast('Error al cargar productos con precio', 'error');
    }
  }, [activePosId, catalogSearch, onToast]);

  useEffect(() => {
    if (showGenerator) loadCatalog();
  }, [showGenerator, loadCatalog]);

  const resetForm = () => {
    setForm({
      name: '',
      offer_kind: 'discount',
      discount_type: 'percent',
      discount_value: '10',
      bundle_offer_price: '',
      status: 'draft',
      notes: '',
    });
    setSelectedProductIds([]);
    setProductQuantities({});
    setPreview([]);
    setCatalogSearch('');
    setEditingOfferId(null);
  };

  const closeGenerator = () => {
    setShowGenerator(false);
    resetForm();
  };

  const openGenerator = () => {
    resetForm();
    setShowGenerator(true);
  };

  const openEditor = (offer) => {
    const items = offer.items || [];
    const quantities = {};
    items.forEach((item) => {
      quantities[item.product_id] = String(item.quantity ?? 1);
    });

    setEditingOfferId(offer.id);
    setForm({
      name: offer.name || '',
      offer_kind: offer.offer_kind || 'discount',
      discount_type: offer.discount_type || 'percent',
      discount_value: offer.discount_value != null ? String(offer.discount_value) : '',
      bundle_offer_price: offer.bundle_offer_price != null ? String(offer.bundle_offer_price) : '',
      status: offer.status || 'draft',
      notes: offer.notes || '',
    });
    setSelectedProductIds(items.map((item) => item.product_id));
    setProductQuantities(quantities);
    setPreview([]);
    setCatalogSearch('');
    setShowGenerator(true);
  };

  const toggleProduct = (product) => {
    setSelectedProductIds((prev) => {
      const exists = prev.includes(product.id);
      if (isDiscountOffer) {
        return exists ? [] : [product.id];
      }
      if (exists) {
        setProductQuantities((qty) => {
          const next = { ...qty };
          delete next[product.id];
          return next;
        });
        return prev.filter((id) => id !== product.id);
      }
      setProductQuantities((qty) => ({
        ...qty,
        [product.id]: qty[product.id] ?? '1',
      }));
      return [...prev, product.id];
    });
    setPreview([]);
  };

  const buildItemsPayload = () =>
    selectedProductIds.map((id) => ({
      product_id: id,
      quantity: parseInt(productQuantities[id], 10) || 1,
    }));

  const validateMultiBundle = () => {
    const offerPrice = parseFloat(form.bundle_offer_price);
    if (Number.isNaN(offerPrice) || offerPrice <= 0) {
      onToast('Indique el precio de la oferta', 'error');
      return false;
    }
    const pct = parseFloat(form.discount_value);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      onToast('Indique un porcentaje de descuento entre 0 y 100', 'error');
      return false;
    }
    const invalidQty = selectedProductIds.some((id) => {
      const qty = parseInt(productQuantities[id], 10);
      return Number.isNaN(qty) || qty < 1;
    });
    if (invalidQty) {
      onToast('Cada producto seleccionado debe tener cantidad al menos 1', 'error');
      return false;
    }
    return true;
  };

  const appendOfferPayloadFields = (payload) => {
    if (isDiscountOffer) {
      payload.discount_type = 'percent';
      payload.discount_value = parseFloat(form.discount_value) || 0;
    }
    if (isMultiProduct) {
      payload.discount_type = 'percent';
      payload.discount_value = parseFloat(form.discount_value) || 0;
      payload.bundle_offer_price = parseFloat(form.bundle_offer_price) || 0;
    }
    return payload;
  };

  const runPreview = async () => {
    if (!activePosId || selectedProductIds.length === 0) {
      onToast('Seleccione un producto con precio en este POS', 'warning');
      return;
    }
    if (isDiscountOffer && selectedProductIds.length !== 1) {
      onToast('En descuento solo puede elegir un producto', 'warning');
      return;
    }
    if (isDiscountOffer) {
      const pct = parseFloat(form.discount_value);
      if (Number.isNaN(pct) || pct < 0 || pct > 100) {
        onToast('Indique un porcentaje entre 0 y 100', 'error');
        return;
      }
    }
    if (isMultiProduct && selectedProductIds.length < 2) {
      onToast('Seleccione al menos dos productos', 'warning');
      return;
    }
    if (isMultiProduct && !validateMultiBundle()) {
      return;
    }

    setPreviewLoading(true);
    try {
      const payload = appendOfferPayloadFields({
        point_of_sale_id: Number(activePosId),
        offer_kind: form.offer_kind,
        items: buildItemsPayload(),
      });

      const res = await salesOfferService.previewOffer(payload);
      if (res.success) {
        const withNames = (res.data || []).map((row) => {
          const product = catalog.find((p) => p.id === row.product_id);
          return { ...row, product_name: product?.name, sku: product?.sku };
        });
        setPreview(withNames);
      }
    } catch (error) {
      onToast(error.response?.data?.message || 'Error al generar vista previa', 'error');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async (activate = false) => {
    if (!form.name.trim()) {
      onToast('Indique el nombre de la oferta', 'error');
      return;
    }
    if (selectedProductIds.length === 0) {
      onToast('Seleccione al menos un producto', 'error');
      return;
    }
    if (isDiscountOffer && selectedProductIds.length !== 1) {
      onToast('En descuento solo puede elegir un producto', 'error');
      return;
    }
    if (isMultiProduct && selectedProductIds.length < 2) {
      onToast('Seleccione al menos dos productos para esta oferta', 'error');
      return;
    }
    if (isDiscountOffer) {
      const pct = parseFloat(form.discount_value);
      if (Number.isNaN(pct) || pct < 0 || pct > 100) {
        onToast('Indique un porcentaje entre 0 y 100', 'error');
        return;
      }
    }
    if (isMultiProduct && !validateMultiBundle()) {
      return;
    }

    setSaving(true);
    try {
      const payload = appendOfferPayloadFields({
        name: form.name.trim(),
        offer_kind: form.offer_kind,
        status: activate ? 'active' : form.status,
        notes: form.notes,
        items: buildItemsPayload(),
      });

      const res = editingOfferId
        ? await salesOfferService.updateOffer(editingOfferId, payload)
        : await salesOfferService.createOffer({
            ...payload,
            point_of_sale_id: Number(activePosId),
          });

      if (res.success) {
        onToast(res.message || (editingOfferId ? 'Oferta actualizada' : 'Oferta creada'), 'success');
        closeGenerator();
        loadOffers();
      }
    } catch (error) {
      onToast(error.response?.data?.message || 'Error al guardar oferta', 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmToggle = async () => {
    if (!toggleTarget) return;
    try {
      const res = await salesOfferService.toggleOffer(toggleTarget.id);
      onToast(res.message, 'success');
      setToggleTarget(null);
      loadOffers();
    } catch (error) {
      onToast(error.response?.data?.message || 'Error al cambiar estado', 'error');
    }
  };

  const formatMoney = (n) =>
    new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(n || 0);

  const statusLabel = (s) => {
    if (s === 'active') return 'Activa';
    if (s === 'cancelled') return 'Cancelada';
    return 'Borrador';
  };

  const offerKindLabel = (kind) => (kind === 'multi_product' ? 'Varios productos' : 'Descuento');

  const discountSummary = (offer) => {
    if (offer.offer_kind === 'multi_product') {
      const price = offer.bundle_offer_price != null ? formatMoney(offer.bundle_offer_price) : '—';
      const pct = offer.discount_value != null ? `${offer.discount_value}%` : '—';
      const itemsCount = offer.items?.length ?? 0;
      return `${itemsCount} productos · ${price} · ${pct} desc.`;
    }
    if (offer.discount_type === 'percent') {
      return `${offer.discount_value}% descuento`;
    }
    if (offer.discount_type === 'fixed_discount') {
      return `S/ ${offer.discount_value} menos`;
    }
    return `Precio fijo S/ ${offer.discount_value}`;
  };

  if (!activePosId) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow-md text-gray-500">
        Elija un punto de venta para gestionar ofertas.
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-end mb-4">
        <MutateGate>
          <button
            type="button"
            onClick={openGenerator}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700"
          >
            + Nueva oferta
          </button>
        </MutateGate>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-gray-500">Cargando…</p>
        ) : offers.length === 0 ? (
          <p className="p-8 text-center text-gray-500">
            No hay ofertas para este punto de venta. Cree una promoción sobre los precios de lista.
          </p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detalle</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {offers.map((offer) => (
                <tr key={offer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-mono text-teal-700">{offer.code}</td>
                  <td className="px-6 py-4 text-sm">{offer.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{offerKindLabel(offer.offer_kind)}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{discountSummary(offer)}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        offer.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : offer.status === 'cancelled'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {statusLabel(offer.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <MutateGate>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => openEditor(offer)}
                          className="text-teal-600 hover:text-teal-800"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => setToggleTarget(offer)}
                          className="text-orange-600 hover:text-orange-800"
                        >
                          {offer.status === 'cancelled' ? 'Reactivar' : 'Cancelar'}
                        </button>
                      </div>
                    </MutateGate>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showGenerator && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                {editingOfferId ? 'Editar oferta' : 'Nueva oferta'}
              </h2>
              <button
                type="button"
                onClick={closeGenerator}
                className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la oferta</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                placeholder="Ej. Promo verano, Combo familiar"
              />
            </div>

            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Tipo de oferta</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {OFFER_KINDS.map((kind) => {
                  const selected = form.offer_kind === kind.value;
                  return (
                    <button
                      key={kind.value}
                      type="button"
                      onClick={() => {
                        setForm((f) => ({ ...f, offer_kind: kind.value }));
                        setPreview([]);
                        setSelectedProductIds([]);
                        setProductQuantities({});
                      }}
                      className={`text-left p-3 rounded-lg border transition-all ${
                        selected
                          ? 'border-teal-300 bg-teal-50 shadow-sm'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <span className="block text-sm font-medium text-gray-800">{kind.label}</span>
                      <span className="block text-xs text-gray-500 mt-1">{kind.description}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {isDiscountOffer && (
              <div className="mb-4 max-w-xs">
                <label className="block text-sm font-medium text-gray-700 mb-1">% Descuento</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.discount_value}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, discount_value: e.target.value }));
                      setPreview([]);
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-500 shrink-0">%</span>
                </div>
              </div>
            )}

            {isMultiProduct && (
              <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Precio de la oferta</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 shrink-0">S/</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.bundle_offer_price}
                      onChange={(e) => {
                        setForm((f) => ({ ...f, bundle_offer_price: e.target.value }));
                        setPreview([]);
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Precio total del combo para el cliente.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">% Descuento general</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={form.discount_value}
                      onChange={(e) => {
                        setForm((f) => ({ ...f, discount_value: e.target.value }));
                        setPreview([]);
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-500 shrink-0">%</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Se aplica igual a todos los productos elegidos.</p>
                </div>
              </div>
            )}

            <div className="mb-4">
              <div className="flex justify-between items-center mb-2 gap-3">
                <label className="text-sm font-medium text-gray-700">
                  {isDiscountOffer
                    ? `Producto (${selectedProductIds.length ? '1 seleccionado' : 'elija uno'})`
                    : `Productos con precio en este POS (${selectedProductIds.length} seleccionados)`}
                  {isDiscountOffer ? (
                    <span className="block text-xs font-normal text-gray-500 mt-0.5">
                      Solo un producto por oferta de descuento.
                    </span>
                  ) : null}
                  {isMultiProduct ? (
                    <span className="block text-xs font-normal text-gray-500 mt-0.5">
                      Marque los productos e indique la cantidad de cada uno en la oferta.
                    </span>
                  ) : null}
                </label>
                <input
                  type="text"
                  placeholder="Buscar…"
                  value={catalogSearch}
                  onChange={(e) => setCatalogSearch(e.target.value)}
                  className="border border-gray-300 rounded-md px-2 py-1 text-sm w-48 focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div className="border border-gray-200 rounded-lg max-h-56 overflow-y-auto divide-y">
                {catalog.length === 0 ? (
                  <p className="p-4 text-sm text-gray-500 text-center">
                    No hay productos con precio. Asigne precios en la pestaña Precios primero.
                  </p>
                ) : (
                  catalog.map((p) => {
                    const checked = selectedProductIds.includes(p.id);
                    return (
                      <div
                        key={p.id}
                        className={`flex flex-wrap items-center gap-3 px-4 py-3 transition-colors ${
                          checked ? 'bg-teal-50/60' : 'hover:bg-gray-50'
                        }`}
                      >
                        <label className="flex items-center gap-3 cursor-pointer flex-1 min-w-[200px]">
                          <input
                            type={isDiscountOffer ? 'radio' : 'checkbox'}
                            name={isDiscountOffer ? 'discount-product' : undefined}
                            className="sr-only"
                            checked={checked}
                            onChange={() => toggleProduct(p)}
                          />
                          <span
                            className={`flex h-5 w-5 shrink-0 items-center justify-center border-2 transition-all ${
                              isDiscountOffer ? 'rounded-full' : 'rounded-md'
                            } ${checked ? 'border-teal-600 bg-teal-600' : 'border-gray-300 bg-white'}`}
                          >
                            {checked ? <CheckIcon /> : null}
                          </span>
                          <span className="text-sm font-mono text-teal-600">{p.sku}</span>
                          <span className="text-sm flex-1">{p.name}</span>
                          <span className="text-sm text-gray-500">Lista: {formatMoney(p.price)}</span>
                        </label>
                        {isMultiProduct && checked && (
                          <div className="flex items-center gap-2 pl-8 sm:pl-0">
                            <span className="text-xs text-gray-500 whitespace-nowrap">Cant.</span>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={productQuantities[p.id] ?? '1'}
                              onChange={(e) => {
                                setProductQuantities((prev) => ({
                                  ...prev,
                                  [p.id]: e.target.value,
                                }));
                                setPreview([]);
                              }}
                              className="w-20 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-500"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              <button
                type="button"
                onClick={runPreview}
                disabled={previewLoading || selectedProductIds.length === 0}
                className="px-4 py-2 border border-teal-600 text-teal-700 rounded-md text-sm disabled:opacity-50"
              >
                {previewLoading ? 'Calculando…' : 'Vista previa'}
              </button>
            </div>

            {preview.length > 0 && (
              <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Producto</th>
                      {isMultiProduct && <th className="px-4 py-2 text-right">Cant.</th>}
                      <th className="px-4 py-2 text-right">Lista</th>
                      <th className="px-4 py-2 text-right">Oferta</th>
                      {isMultiProduct && <th className="px-4 py-2 text-right">Subtotal</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {preview.map((row) => (
                      <tr key={row.product_id}>
                        <td className="px-4 py-2">{row.product_name || row.product_id}</td>
                        {isMultiProduct && (
                          <td className="px-4 py-2 text-right text-gray-600">{row.quantity ?? 1}</td>
                        )}
                        <td className="px-4 py-2 text-right">{formatMoney(row.base_price)}</td>
                        <td className="px-4 py-2 text-right font-medium text-orange-600">
                          {formatMoney(row.offer_price)}
                        </td>
                        {isMultiProduct && (
                          <td className="px-4 py-2 text-right font-medium">
                            {formatMoney(row.line_total)}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  {isMultiProduct && form.bundle_offer_price !== '' && (
                    <tfoot className="bg-gray-50 border-t">
                      <tr>
                        <td
                          colSpan={isMultiProduct ? 5 : 3}
                          className="px-4 py-2 text-right text-sm font-medium text-gray-700"
                        >
                          Precio de la oferta: {formatMoney(parseFloat(form.bundle_offer_price) || 0)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <button
                type="button"
                onClick={closeGenerator}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm"
              >
                Cancelar
              </button>
              {editingOfferId ? (
                <button
                  type="button"
                  onClick={() => handleSave(false)}
                  disabled={saving}
                  className="px-4 py-2 bg-teal-600 text-white rounded-md text-sm disabled:opacity-50"
                >
                  {saving ? 'Guardando…' : 'Guardar cambios'}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => handleSave(false)}
                    disabled={saving}
                    className="px-4 py-2 bg-gray-800 text-white rounded-md text-sm disabled:opacity-50"
                  >
                    Guardar borrador
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSave(true)}
                    disabled={saving}
                    className="px-4 py-2 bg-teal-600 text-white rounded-md text-sm disabled:opacity-50"
                  >
                    Activar oferta
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!toggleTarget}
        onClose={() => setToggleTarget(null)}
        onConfirm={confirmToggle}
        title={toggleTarget?.status === 'cancelled' ? 'Reactivar oferta' : 'Cancelar oferta'}
        message={
          toggleTarget
            ? `¿${toggleTarget.status === 'cancelled' ? 'Reactivar' : 'Cancelar'} "${toggleTarget.name}"?`
            : ''
        }
        confirmText="Sí"
        cancelText="No"
      />
    </>
  );
};

export default PriceListOffersSection;
